/**
 * A fila da caixa-preta no aparelho. Pura: sem DOM, sem rede, sem relógio.
 *
 * Existe separada do envio porque o que é difícil aqui NEVER é mandar — é decidir
 * o que fazer quando não dá para mandar. Rede móvel cai, aba vai para o segundo
 * plano, e o servidor recusa. Nesses casos a fila enche, e uma fila que enche em
 * SILÊNCIO é a pior coisa que pode acontecer com um dado de diagnóstico: "o ímã
 * caiu 3 vezes" pode significar 3 ou 300, e NEVER há como saber qual.
 *
 * Por isso o descarte é CONTADO e vira evento (`queue.dropped`). Uma medição que
 * sabe o que perdeu continua sendo medição; uma que não sabe é chute com número.
 */
import type { TelemetryEvent } from '@game/telemetry';

/**
 * Teto de eventos guardados. Uma partida normal produz muito menos: o teto existe
 * para o caso patológico — aba esquecida aberta, servidor fora do ar por minutos.
 */
export const DEFAULT_CAPACITY = 500;

export class TelemetryQueue {
  private readonly events: TelemetryEvent[] = [];
  private droppedSincePush = 0;

  constructor(private readonly capacity = DEFAULT_CAPACITY) {}

  get size(): number {
    return this.events.length;
  }

  /** Quantos eventos a fila jogou fora desde o último `drain`. */
  get dropped(): number {
    return this.droppedSincePush;
  }

  /**
   * Guarda um evento. Cheia, descarta o NOVO e conta.
   *
   * Descartar o novo e NEVER o antigo é decisão: o começo da sessão carrega o
   * `session.start`, que descreve o aparelho, e sem ele todos os outros eventos
   * viram números sem contexto. Perder o fim de uma sessão patológica custa menos
   * que perder a identidade do aparelho inteiro.
   */
  push(event: TelemetryEvent): void {
    if (this.events.length >= this.capacity) {
      this.droppedSincePush += 1;
      return;
    }
    this.events.push(event);
  }

  /**
   * Tira tudo o que está na fila para mandar. Quando houve descarte, o próprio
   * `queue.dropped` sai junto — e ele NEVER conta contra o teto, porque a linha
   * que confessa a perda é justamente a que NEVER pode ser perdida.
   */
  drain(atMs: number): TelemetryEvent[] {
    const out = this.events.splice(0, this.events.length);
    if (this.droppedSincePush > 0) {
      out.push({ t: atMs, kind: 'queue.dropped', dropped: this.droppedSincePush });
      this.droppedSincePush = 0;
    }
    return out;
  }

  /**
   * Devolve os eventos à fila depois de um envio que falhou.
   *
   * O que NEVER couber é contado como descarte, e NEVER descartado calado — é o
   * caso do celular que perdeu o sinal no meio da partida, que é exatamente a
   * condição que mais interessa medir.
   */
  requeue(events: readonly TelemetryEvent[]): void {
    for (const event of events) {
      // O `queue.dropped` que voltou NEVER é reenfileirado como evento: ele seria
      // contado duas vezes no dia em que o envio seguinte também falhasse.
      if (event.kind === 'queue.dropped') {
        this.droppedSincePush += event.dropped;
        continue;
      }
      if (this.events.length >= this.capacity) {
        this.droppedSincePush += 1;
        continue;
      }
      this.events.push(event);
    }
    // Ordem restaurada: o que voltou é mais ANTIGO que o que entrou enquanto o
    // envio estava em curso, e uma fila fora de ordem estraga toda leitura de `t`.
    this.events.sort((a, b) => a.t - b.t);
  }
}
